import React, { useContext, useMemo } from "react"
import { useWatch } from "react-hook-form"

import classNames from "classnames"

import { exists } from "shared/lib/exists"
import { SelectOption, isSelectOption } from "shared/types/Select"
import { InputContext } from "shared/ui/input/Input"

interface ValueProps<T>
  extends Omit<
    React.PropsWithoutRef<React.HTMLProps<HTMLDivElement>>,
    "children" | "value"
  > {
  value?: T | T[] | SelectOption<T> | SelectOption<T>[] | null | undefined
  options?: SelectOption<T>[]
}

function getSelectOptionLabel<T>(
  value: T,
  options?: SelectOption<T>[]
): string {
  if (!exists(value)) return "-"

  const selectedOption = options?.find(o => o.value === value)
  if (isSelectOption(selectedOption)) return selectedOption.label
  if (isSelectOption(value)) return value.label
  if (typeof value === "string") return value

  return JSON.stringify(value)
}

function ValueBase<T>({ className, options, value, ...props }: ValueProps<T>) {
  const resolvedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return (
        value
          .map(v => getSelectOptionLabel(v, options))
          .filter(Boolean) as string[]
      ).join(", ")
    }

    return getSelectOptionLabel(value, options)
  }, [value, options])

  return (
    <div {...props} className={classNames("input__value", className)}>
      {resolvedValue}
    </div>
  )
}

function HookFormValue<T>({ value: externalValue, ...props }: ValueProps<T>) {
  const { name } = useContext(InputContext)
  const value = useWatch({ name })

  return <ValueBase {...props} value={externalValue || value} />
}

function ValueComponent<T>(props: ValueProps<T>) {
  const { name } = useContext(InputContext)
  if (name) {
    return <HookFormValue {...props} />
  }
  return <ValueBase {...props} />
}

export const SelectValue = React.memo(ValueComponent) as typeof ValueComponent
